// Desc: UserSearchProvider is a context provider that provides the user list to the search components that need it.
// Since the searchbar is used in multiple components, it is better to have a single source of truth for the user list.
import React, { createContext, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector, shallowEqual } from "react-redux";

const UserSearchContext = createContext();

export const useUserSearchContext = () => useContext(UserSearchContext);

export const UserSearchProvider = ({ children }) => {
  const { get_employee_org_category, get_employees, coreData } = useSelector(
    (state) => ({
      get_employee_org_category: state.employee_category.employee_org_category,
      get_employees: state.employees,
      coreData: state.audit.core_data,
    }),
    shallowEqual
  );

  const userMap = (usersList, employeeList) => {
    if (!usersList || !employeeList) return [];
    const list = usersList?.map((user) => {
      const employeeObj = employeeList?.employees?.find(
        (emp) => emp.id === user?.employee_id
      );

      return {
        key: user?.employee_id,
        value: user?.employee_id,
        text: `${employeeObj?.first_name} ${employeeObj?.last_name}`,
        id: user?.id,
        userCategory: user?.category,
      };
    });

    const empList = employeeList?.employees;
    const inactiveList = empList?.filter(
      (emp) => emp.status === 99 || emp.status === 98 || emp.status === 108
    );

    const enabledLogins = empList?.filter((emp) => emp.enable_login === true);

    const filteredList = list.filter(
      (emp) => !inactiveList.find((inactive) => inactive.id === emp.key)
    );

    filteredList.unshift({ key: -1, value: -1, text: "Select User" });
    return filteredList;
  };

  const groupMap = (categoryList) => {
    // Create a list of options from the category list.
    // Include the primary group name & primary group id in the object.
    const groups = [];
    categoryList?.forEach((cat) => {
      // If it is a demographic group, don't include it in the list of groups.
      if (cat?.demographic === true) return;

      const groupName = cat?.name;
      const groupId = cat?.id;
      // 1) Map the main options
      cat.options?.forEach((opt) => {
        groups.push({
          key:   opt.id,
          value: opt.id,
          text:  opt.name,
          color: opt.color,
          groupId,
          groupName,
        });
      });
      // 2) Map the sub-options
      cat.options?.forEach((opt) => {
        if (opt?.subfactors && Array.isArray(opt?.subfactors)) {
          opt.subfactors.forEach((sf) => {
            groups.push({
              key:              sf.id,
              value:            sf.id,
              text:             sf.name,
              color:            sf.color || opt.color,
              groupId,
              groupName,
              parentOptionId:   opt.id,
              parentOptionName: opt.name,
            });
          });
        }
      });
    });
    return groups;
  };

  const value = useMemo(() => {
    const userList = userMap(get_employee_org_category, get_employees);
    const groupList = groupMap(coreData?.categories?.[0]?.categories);

    return { userList, groupList };
  }, [get_employee_org_category, get_employees, coreData]);

  return (
    <UserSearchContext.Provider value={value}>
      {children}
    </UserSearchContext.Provider>
  );
};

UserSearchProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
